import React, { useState, useEffect, useRef, useContext } from 'react';

import {
	startCamera,
	connectToElement,
	setScanHandler,
	releaseScreen,
	pauseDecoding,
	resumeDecoding,
	refreshLayout,
	fixUPCEAN,
	getLastInitError,
	BARKODER_CONTAINER_ID,
	BARKODER_LICENSE_ERROR_CODE,
} from "../../scanSdk/barkoderSdk";
import { isValidMod10Barcode } from "../../scanSdk/checkDigit";

import { describePreviewState, reviveVideos } from '../../scanSdk/scanHost';
//※スキャンSDK名・解像度の表示（一時的に非表示。戻すときは import / 定数 / 表示 の3箇所をまとめて有効化する
// import { getCameraResolution } from '../../scanSdk/cameraResolutionSetting';

import styles from '../../styles/BarcodeScanner.module.scss';
import {AnimatePresence, motion} from 'framer-motion';
import { useUserAPI } from '../../components/useUserAPI';
import { ShoppingStart } from '../../components/ShoppingStart';
import { SharedInfoContext } from '../../components/SharedInfoContextProvider';

//スキャンを止めたまま読み取り値が来ない場合に、再開するまでの待ち時間
const SCAN_STALL_TIMEOUT = 5000;

//カメラ映像（プレビュー）が出るまでの監視間隔と待ち上限
//※黙って諦めると、スキャン枠を出す is_show が付かないまま画面が黒く残り、
//　読み込み中なのか失敗なのか分からなくなるため、超えたらエラーとして扱う
const PREVIEW_WAIT_INTERVAL = 80;
const PREVIEW_WAIT_TIMEOUT = 10000;

//プレビューが出ないときに復帰を試みるタイミング
//※スロットへの付け替えでvideoが素材を手放すと readyState が 0 のまま戻らない端末がある。
//　この場合プレビューが出ないため、映像確認後に行う refreshLayout()（＝D-21の手当て）にも
//　到達できない。安い順に、ストリームの付け直し → スキャナーの組み直し、と試す
const PREVIEW_REVIVE_AFTER = 1500;
const PREVIEW_RELAYOUT_AFTER = 3500;

function BarkoderScanner(props) {

//スキャン画面右下の表示用（どのSDK・どの解像度で動いているか分かるようにする
//※スキャンSDK名・解像度の表示（一時的に非表示
// const cameraResolution = getCameraResolution();

const ShoppingStartContext = useContext(ShoppingStart);
const sharedInfo = useContext(SharedInfoContext);
const { bandColor, updateBandColor } = useContext(SharedInfoContext);

const [firstScan, setFirstScan] = useState(true);
//取得バーコード検証用
const [barcode, setBarcode] = useState();

const slotRef = useRef(null);
//アンマウント済みフラグ
const unmountedRef = useRef(false);
//プレビュー監視タイマー
const previewTimerRef = useRef(null);

//スキャン結果の受け取り口
//※常駐スキャナーへ登録するため、参照が変わらない関数にしておく
const onDetectedRef = useRef(null);
const scanHandlerRef = useRef((result) => {
	if (onDetectedRef.current) onDetectedRef.current(result);
});


//----------------------------------------
//barkoder：スキャン結果
//----------------------------------------

onDetectedRef.current = (result) => {
	let code = result?.rawData ?? result?.textualData;
	if (!code) return;

	//JAN13補正
	code = fixUPCEAN(code);
	if (!code) return;

	console.log("-------------------------");
	console.log("【barkoder】detected");
	console.log("barcodeTypeName: " + result?.barcodeTypeName);
	console.log("data: " + code);
	console.log("-------------------------");

	//同一バーコードの連続検知を防ぐためデコード停止
	pauseDecoding();

	//スキャン不可
	props.setScanFlg(true);

	//チェックデジット検証（NGなら商品登録APIを呼ばない
	//※モーダルを閉じると setScanFlg(false) が呼ばれてスキャンが再開する
	if (isValidMod10Barcode(code) === false) {
		console.log("【barkoder】チェックデジットNG: " + code);
		props.setSoundState("se07_scan");
		props.setErrFlg(true);
		props.setErr([true,``, process.env.REACT_APP_ERROR_MESSAGE_CHECKDIGIT]);
		return;
	}

	setBarcode(code);
};


//----------------------------------------
//barkoder：カメラ表示開始
//----------------------------------------
// カメラはスタート画面／スキャンボタン押下時に起動済みのため、
// ここでは常駐コンテナを画面内のスロットへ付け替えるだけ。
// 未起動の場合のみここで起動する。

const showCamera = async () => {
	//先にスロットへ付け替え（起動済みなら即座に表示される
	connectToElement(slotRef.current);

	const started = await startCamera();
	if (unmountedRef.current) return;

	if (!started) {
		//初期化の失敗理由が分かっていればそれを出す（実機ではログを取れないため
		onScanError(new Error(getLastInitError() || "barkoder camera start failed"));
		return;
	}

	//起動直後はvideoが後から生成されるため、付け替えし直す
	connectToElement(slotRef.current);

	//デコード開始
	resumeDecoding();

	waitForCameraPreview();
};


//----------------------------------------
//barkoder：プレビュー表示検知
//----------------------------------------

const waitForCameraPreview = () => {
	if (previewTimerRef.current) {
		clearInterval(previewTimerRef.current);
		previewTimerRef.current = null;
	}

	let retry = 0;
	let revived = false;
	let relaidOut = false;

	previewTimerRef.current = setInterval(() => {
		if (unmountedRef.current) {
			clearInterval(previewTimerRef.current);
			previewTimerRef.current = null;
			return;
		}

		const video = slotRef.current?.querySelector("video");
		if (!video || video.readyState < 3 || video.videoWidth === 0) {
			retry++;
			const elapsed = retry * PREVIEW_WAIT_INTERVAL;

			//① videoが素材を手放しているだけなら、ストリームを付け直せば戻る
			if (!revived && elapsed >= PREVIEW_REVIVE_AFTER) {
				revived = true;
				if (reviveVideos(slotRef.current)) {
					console.log("【barkoder】videoが停止していたためストリームを付け直した");
				}
			}

			//② それでも出ない場合はスキャナーを組み直す（D-21と同じ手当て
			if (!relaidOut && elapsed >= PREVIEW_RELAYOUT_AFTER) {
				relaidOut = true;
				console.log("【barkoder】プレビューが出ないため組み直す");
				refreshLayout();
			}

			if (elapsed >= PREVIEW_WAIT_TIMEOUT) {
				clearInterval(previewTimerRef.current);
				previewTimerRef.current = null;
				//3条件のどれが欠けているかで対処が変わるため、状態を載せる
				const state = describePreviewState(slotRef.current, BARKODER_CONTAINER_ID);
				console.log("【barkoder】camera preview timeout: " + state);
				onScanError(new Error("barkoder camera preview timeout / " + state));
			}
			return;
		}

		console.log("【barkoder】camera preview shown");

		clearInterval(previewTimerRef.current);
		previewTimerRef.current = null;

		//カメラ映像が出てからスキャン枠を表示
		//※refreshLayout はスキャナーを組み直すことがあるので、枠は先に出しておく
		document.getElementById('scanBox')?.classList.add(`${styles.is_show}`);

		//映像が出てからSDKにレイアウトを整えさせる
		//※これが無いと、退避状態のサイズのままvideoが小さく残る端末がある
		refreshLayout();
	}, PREVIEW_WAIT_INTERVAL);
};


//----------------------------------------
//barkoder：スキャン画面から離れる
//----------------------------------------
// scandit と同じく、スキャン画面から離れたらカメラを閉じる

const hideCamera = () => {
	if (previewTimerRef.current) {
		clearInterval(previewTimerRef.current);
		previewTimerRef.current = null;
	}

	//デコード停止・コンテナ退避・カメラ停止（次の画面が使用中なら何もしない
	const released = releaseScreen(scanHandlerRef.current, slotRef.current);

	//スキャン枠を隠すのは、自分がまだ表示を持っていたときだけ。
	//※画面遷移中は「次の画面のマウント → 前の画面のアンマウント」の順になるため、
	//　無条件に消すと、次の画面が既に表示した枠まで消してしまい一瞬枠が欠ける
	if (released) {
		document.getElementById('scanBox')?.classList.remove(`${styles.is_show}`);
	}
};


//----------------------------------------
//barkoderエラー表示
//----------------------------------------

const onScanError = (error) => {
	//ライセンスの準備ができなかった（barkoderSdk.js の「ライセンスの準備の状態」で判定）
	//※技術的なメッセージをそのまま出さず、利用者が対処できる文言にする。
	//　スキャン不可にしておくと、閉じたときの setScanFlg(false) で resumeDecoding() が呼ばれ、
	//　ライセンスの確認をやり直す（必要ならリロードする。barkoderSdk.js の説明を参照
	if (error && error.code === BARKODER_LICENSE_ERROR_CODE) {
		console.log("error: " + error);
		props.setScanFlg(true);
		props.setErrFlg(true);
		props.setErr([true,``, error.retryOver ? process.env.REACT_APP_ERROR_MESSAGE_BARKODER_LICENSE_RETRY_OVER : process.env.REACT_APP_ERROR_MESSAGE_BARKODER_LICENSE]);

		//ログアップロードAPI（判定の経路を含めて残し、発生件数を後から追えるようにする
		loguploadAPI(sharedInfo.transactionno,"barkoder:error, " + error);
		return;
	}

	if (String(error).indexOf("NotAllowedError") !== -1 || String(error).indexOf("Permission denied") !== -1) {
		console.log("error: " + error);
		props.setErrFlg(true);
		props.setErr([true,``, process.env.REACT_APP_ERROR_MESSAGE_SCANDIT_NOTALLOW]);

		//ログアップロードAPI
		loguploadAPI(sharedInfo.transactionno,"barkoder:error, " + error);

	}else if(String(error) !== "undefined" && String(error) !== undefined && String(error) !== ""){
		console.log("error: " + error);
		props.setErrFlg(true);
		let err_msg = ";;"+error+";;;";
		props.setErr([true,``, err_msg]);

		//ログアップロードAPI
		loguploadAPI(sharedInfo.transactionno,"barkoder:error, " + error);
	}
}


//----------------------------------------
//スキャン有効化
//----------------------------------------

useEffect(() => {
	if(firstScan === true){
		setFirstScan(false);
	}else{
		console.log("props.scanFlg: " + props.scanFlg);
		// props.scanFlg true: スキャン不可 false:スキャン可
		if(props.scanFlg === false && firstScan === false){
			setBarcode();
			//デコード再開
			resumeDecoding();
		}
	}
}, [props.scanFlg]);


//----------------------------------------
//ログアップロードAPI
//----------------------------------------

const { loguploadAPI, data: loguploadData, loading: loguploadLoading, error: loguploadError } = useUserAPI();

//ログアップロードAPI：レスポンス
useEffect(() => {
	if(loguploadData){
		console.log(loguploadData);
		if (loguploadData.isSuccess == true) { //SUCCESS
			console.log(`【loguploadAPI】 isSuccess: ${loguploadData.isSuccess}`);
		}else{ //ERROR
			console.log(`【loguploadAPI】 isSuccess: ${loguploadData.isSuccess}`);
			console.log(`【loguploadAPI】 errorCode: ${loguploadData.errorCode}`);
			console.log(`【loguploadAPI】 errorMessage: ${loguploadData.errorMessage}`);
		}
	}
}, [loguploadData]);


//----------------------------------------
//初期化
//----------------------------------------

useEffect(() => {
	unmountedRef.current = false;

	//arrows We 対応
	if(window.innerWidth >= 320){
		document.documentElement.style.zoom = window.innerWidth / 390;
		var zratio = window.innerWidth / 390;
	}else{
		document.documentElement.style.zoom = 1;
		var zratio = 1;
	}
	let hratio = window.innerHeight / (window.innerHeight * zratio);
	let realheight = window.innerHeight * hratio;
	let elementA = document.getElementById('scan');
	if (elementA) {
		elementA.style.height = realheight + 'px';
	}

	//スキャン結果の受け取り先として登録
	setScanHandler("barcode", scanHandlerRef.current, onScanError);

	//カメラ表示（スタート画面で起動済みのカメラを使い回す
	showCamera();

	return () => {
		unmountedRef.current = true;
		hideCamera();
	};
}, []);


//----------------------------------------
//商品登録API
//----------------------------------------

const { shoppingPurchaseAPI, data: shoppingPurchaseData, loading: shoppingPurchaseLoading, error: shoppingPurchaseError, scnflg: shoppingPurchaseScanFlg } = useUserAPI();
//API を呼び出せる状態かを管理するフラグ：初期値は true で、API を呼び出すたびに false に設定し、タイマーで 1 秒後に true に戻す
const [isApiCallAllowed, setIsApiCallAllowed] = useState(true);

//商品登録API：呼び出し制限
useEffect(() => {
	console.log("barcode: " + barcode);
	console.log("isApiCallAllowed: " + isApiCallAllowed);
	if (barcode && isApiCallAllowed && !shoppingPurchaseLoading) {
		console.log("【商品登録API call】---------------------------");
		shoppingPurchaseAPI(barcode, ShoppingStartContext.data.token);
		console.log("【/商品登録API call】---------------------------");

		// API呼び出し制御をfalseにし、1秒後にtrueに戻す
		//※このタイマーをクリーンアップで解除してはいけない。
		//　このeffectは barcode が変わるたびに再実行され、その直前に前回のクリーンアップが走る。
		//　API呼び出しから1秒以内に barcode が変化するとタイマーが解除され、
		//　isApiCallAllowed が false のまま戻らなくなり、以降ずっとAPIを呼べなくなる
		setIsApiCallAllowed(false);
		setTimeout(() => {
			setIsApiCallAllowed(true);
		}, 1000);
	}else {
		//どの条件でAPIを呼ばなかったのかを出す。
		//※読み取り値がまだ無いだけの状態（画面を開いた直後や、検知値をクリアした直後）でも
		//　ここを通る。以前は一律で「再呼び出し制限中」と出していたため、
		//　制限で止まっているように見えて紛らわしかった
		if (!barcode) {
			console.log("【商品登録API】読み取り値なし（待機中）");
		} else if (shoppingPurchaseLoading) {
			console.log("【商品登録API】通信中のため見送り");
		} else {
			console.log("【商品登録API call】再呼び出し制限中");
		}

		//APIを呼ばずに抜けた場合はレスポンスもエラーモーダルも発生しないため、
		//検知時に止めたスキャン（pauseDecoding／setScanFlg(true)）を戻す契機が無く画面が固まる。
		//スキャン状態を戻して読み直せるようにする（通信中はレスポンス側で戻すので触らない
		if (barcode && !shoppingPurchaseLoading) {
			setBarcode();
			props.setScanFlg(false);
		}
	}
}, [barcode]);


//----------------------------------------
//スキャン停止からの復帰（保険
//----------------------------------------
// 検知時は「スキャンを止める → 読み取り値をstateへ渡す」の順で処理している。
// scandit は停止処理が非同期（enableScanning(false) → enableCamera(false)）のため、
// 低スペック端末でここが失敗・遅延すると、値がstateへ渡らないまま終わることがある。
// その場合 scanFlg が true のまま戻す契機が無く、以降スキャンできなくなる。
// 「スキャンを止めているのに読み取り値が無い」状態が続いたら再開させる。
//
// ※ここでの clearTimeout は正しい。条件が変わったら判定をやり直したいため。
// 　上のAPI呼び出し制限のタイマーとは意図が逆なので混同しないこと

useEffect(() => {
	//スキャン可・エラーモーダル表示中・読み取り値ありは対象外
	if (props.scanFlg !== true) return;
	if (props.errFlg === true) return;
	if (barcode) return;

	const timer = setTimeout(() => {
		console.log("【scan】スキャンを止めたまま読み取り値が来ないため再開する");
		props.setScanFlg(false);
	}, SCAN_STALL_TIMEOUT);

	return () => clearTimeout(timer);
}, [props.scanFlg, props.errFlg, barcode]);

//商品登録API：レスポンス
useEffect(() => {
	if(shoppingPurchaseData){
		if (shoppingPurchaseData.data) { //SUCCESS
			props.setSoundState("se07_scan");

			console.log(`【shoppingPurchaseAPI】 receiptKanji: ${shoppingPurchaseData.data.receiptKanji}`);
			console.log(`【shoppingPurchaseAPI】 sellPrice: ${shoppingPurchaseData.data.sellPrice}`);
			console.log(`【shoppingPurchaseAPI】 discountSellPrice: ${shoppingPurchaseData.data.discountSellPrice}`);

			props.setShoppingData(shoppingPurchaseData);
			setScanItem(true);
			setTimeout(function(){
				setScanItem(false);
				//カメラを閉じてコンテナを退避
				hideCamera();
				props.setPage("shopping");
			}, 2500);
		}else{ //ERROR
			console.log(`【shoppingPurchaseAPI】 errorCode: ${shoppingPurchaseData.errorCode}`);
			console.log(`【shoppingPurchaseAPI】 errorMessage: ${shoppingPurchaseData.errorMessage}`);
			console.log(`【shoppingPurchaseAPI】 isSuccess: ${shoppingPurchaseData.isSuccess}`);

			props.setSoundState("se07_scan");

			if(shoppingPurchaseData.errorCode === "26"){
				//トークン不正、トークン有効期限切れ のためエラー表示後にアプリ起動後の画面へ（再読込
				props.setErrFlg(true);
				props.setErr([true,`${shoppingPurchaseData.errorCode}`, process.env.REACT_APP_ERROR_MESSAGE_26]);
			}else if(shoppingPurchaseData.errorCode === "27"){
				//トークン不正、トークン有効期限切れ のためエラー表示後にアプリ起動後の画面へ（再読込
				props.setErrFlg(true);
				props.setErr([true,`${shoppingPurchaseData.errorCode}`, process.env.REACT_APP_ERROR_MESSAGE_27]);
			}else{
				props.setErrFlg(true);
				props.setErr([true,`${shoppingPurchaseData.errorCode}`,`${shoppingPurchaseData.errorMessage}`]);
			}
		}
	}
	if(shoppingPurchaseError){
		console.log("shoppingPurchaseError");
		console.log(shoppingPurchaseError);
		props.setErrFlg(true);
		props.setErr([true,``, process.env.REACT_APP_ERROR_MESSAGE_TIMEOUT]);
	}
	if(shoppingPurchaseLoading){
		document.querySelectorAll('.loadingBox01').forEach(function(element) {
			element.classList.add('is_show');
		});
		document.querySelectorAll('.loadingBox01_cover').forEach(function(element) {
			element.classList.add('is_show');
		});
	}else{
		document.querySelectorAll('.loadingBox01').forEach(function(element) {
			element.classList.remove('is_show');
		});
		document.querySelectorAll('.loadingBox01_cover').forEach(function(element) {
			element.classList.remove('is_show');
		});
	}
}, [shoppingPurchaseData,shoppingPurchaseLoading,shoppingPurchaseError,shoppingPurchaseScanFlg]);


//----------------------------------------
//スキャン閉じる（買物一覧画面へ
//----------------------------------------

const onScanClose = () => {
	//カメラを閉じてコンテナを退避
	hideCamera();

	props.setPage("shopping");
	props.setSoundState("se08_se");
}

const [scanItem, setScanItem] = useState(false)

const motionConfig = {
	initial: {
		y: -20,
		opacity: 0,
	},
	animate: {
		y: 0,
		opacity: 1,
	},
	hidden: {
		y: 20,
		opacity: 0,
	},
};


return (
<div id="scan" className="container l_contents">

<header id="header02" className={`l_header02 ${bandColor}`}>
	<button href="" className="header02_backBtn" onClick={onScanClose}>
		<img src="/images/icn_back01_white.svg" alt="" width="24" height="24"/>
	</button>
	<p className="header02_tit">スキャン中</p>
</header>

<div className={styles.msgBox}>
	<p className={styles.msgBox_txt}>商品のバーコードを<br/>枠内に入れてください</p>
</div>

<div id="scanBox" className={styles.scanBox}>
	<div id="barcode-scanner-area" className={styles.scanArea}></div>
	<div className={styles.scanBoxBlack}></div>
	<div className={styles.scanCover}></div>
	<div ref={slotRef} className={styles.sdkScanView}></div>
	{/* ※スキャンSDK名・解像度の表示（一時的に非表示 */}
	{/* <div className={styles.sdkScanName}>barkoder + {cameraResolution}</div> */}
	{/* ※barkoder使用時の目印（一時的な表示 */}
	<div className={styles.sdkScanName}>NAVY</div>
</div>

<AnimatePresence>
	{scanItem && (
		<motion.div id="addItem" className={styles.addItem}
		initial={'initial'}
		animate={'animate'}
		exit={'hidden'}
		variants={motionConfig}
		transition={{duration: 0.4, ease: 'easeOut'}}
		>
				<div className={styles.addItem_head}>
					<p className={styles.addItem_head_txt}>
						<span className={styles.addItem_head_check}>
							<img src="/images/icn_check_aeon.svg" alt="" width="24" height="24" />
						</span>
						商品を追加しました
					</p>
				</div>
				<div className={styles.addItem_box}>
					<div className={styles.addItem_in}>
						<div className={styles.addItem_img}>
							<img src={shoppingPurchaseData.data.itemUrl ? shoppingPurchaseData.data.itemUrl : '/images/noimage.png'} alt="" width="40" height="40"/>
						</div>
						<p className={styles.addItem_name}>{shoppingPurchaseData.data.receiptKanji}</p>
						<div className={styles.addItem_priceBox}>
							<p className={styles.addItem_price}>
								<span className={`${styles.addItem_price_default} ${shoppingPurchaseData.data.discountSellPrice ? styles.cancel : ''}`}><small>¥</small>
								{
									(() => {
										return (
											(shoppingPurchaseData.data.sellPrice).toLocaleString('ja-JP')
										)
									})()
								}
								</span>
							</p>
						</div>
					</div>
					{
						(() => {
							if (shoppingPurchaseData.data.discountType === 1) {
								return(
									<div className={styles.addItem_discount}>
										<p className={styles.addItem_discount_txt}>レジにて<strong>{(shoppingPurchaseData.shoppingPurchaseData.data.discountAmount).toLocaleString('ja-JP')}</strong>円引き</p>
										<p className={styles.addItem_discount_price}><small>¥</small>{(shoppingPurchaseData.data.discountSellPrice).toLocaleString('ja-JP')}</p>
									</div>
								)
							}else if (shoppingPurchaseData.data.discountType === 2) {
								return(
									<div className={styles.addItem_discount}>
										<p className={styles.addItem_discount_txt}>レジにて<strong>{shoppingPurchaseData.data.discountAmount}</strong>%OFF</p>
										<p className={styles.addItem_discount_price}><small>¥</small>{(shoppingPurchaseData.data.discountSellPrice).toLocaleString('ja-JP')}</p>
									</div>
								)
							}else if (shoppingPurchaseData.data.discountType === 3) {
								return(
									<div className={styles.addItem_discount}>
										<p className={styles.addItem_discount_txt}>レジにて</p>
										<p className={styles.addItem_discount_price}><small>¥</small>{(shoppingPurchaseData.data.discountSellPrice).toLocaleString('ja-JP')}</p>
									</div>
								)
							}
						})()
					}
				</div>
		</motion.div>
	)}
</AnimatePresence>
</div>
	)
}

export default BarkoderScanner
